import { makeAutoObservable } from "mobx";

import { ApiConnector, RequestError } from "@/api/api-connector";
import { maybeReportError } from "@/app/errors";
import { apiRoute } from "@/config/config";

import {
  apiGetUsersMe,
  PermissionBit,
  PermissionMetadataResponse,
  PostApiV1AuthFreshLoginResponse,
  UserRole,
} from "../../api";

const apiBase = `${apiRoute}/api/v1`;
const adminUsername = "admin";
const accessTokenKey = "devtools:adminAccessToken";
const legacyRefreshTokenKey = "devtools:adminRefreshToken";

/**
 * Handles the dev-tool's permissions flow: looks up the current user's access
 * policy via their regular session, then mutates it as the `admin` user.
 *
 * Bypasses the regular ApiConnector for the admin-side calls so the logged-in
 * user's session is not affected, and holds an admin access token in
 * localStorage so the dev-tool only prompts for the password once per its
 * 15-minute lifetime — `applyToCurrentUser` reloads the page, so an in-memory
 * cache would not survive a single change.
 *
 * The token comes from `/auth/fresh-login` because `PUT /policies/{uuid}`
 * requires a *fresh* one; a token minted from a refresh token never is. That
 * also keeps a long-lived admin refresh token out of localStorage.
 *
 * Public methods self-orchestrate: if no admin session is cached they surface
 * the password prompt via observables and resume once `submitPassword` succeeds.
 */
export class PermissionsClient {
  isWorking = false;
  needsPassword = false;
  passwordError: string | null = null;
  /** Last failure of {@link applyToCurrentUser}, for the sheet to render. */
  error: unknown = null;

  private passwordRequest: {
    resolve: () => void;
    reject: (e: Error) => void;
  } | null = null;

  constructor(private readonly apiConnector: ApiConnector) {
    makeAutoObservable(this);

    // Evicted on sight: the dev-tool no longer mints tokens from a refresh
    // token, and a long-lived admin one should not linger in localStorage.
    localStorage.removeItem(legacyRefreshTokenKey);
  }

  get isAdmin(): boolean {
    return this.apiConnector.username === adminUsername;
  }

  /**
   * Apply `permissions` to the currently logged-in user's access policy and
   * reload the page so the new permissions take effect. Silent no-op if the
   * user dismisses the admin-password prompt.
   */
  async applyToCurrentUser(permissions: PermissionBit[]): Promise<void> {
    this.error = null;

    try {
      // Rotates like any other read: after a 15-minute idle the session token
      // is expired too, and refusing to refresh it here fails the whole flow
      // before the admin call that carries its own re-authentication.
      const { data } = await apiGetUsersMe({
        meta: { rotateOn401: "always", reauthOn428: false },
      });
      const policyUuid = data?.access_policy?.uuid;
      if (!policyUuid) throw new Error("No access policy found");

      await this.runAsAdmin((token) =>
        this.adminFetch(`policies/${policyUuid}`, token, {
          method: "PUT",
          body: JSON.stringify({ permissions }),
        }),
      );
    } catch (e) {
      if (e instanceof PasswordPromptCancelled) return;
      // Held for the sheet to render rather than thrown: an unhandled
      // rejection here only reaches the console, which reads as the tool
      // doing nothing at all.
      maybeReportError(e);
      this.error = e;
      return;
    }
    window.location.reload();
  }

  async loadPermissionsMetadata(): Promise<PermissionMetadataResponse> {
    // Unlike `applyToCurrentUser`, a failure here is left to propagate: the
    // modal that asked for it is already open, so the global error dialog is
    // the only place with anything to show.
    return this.runAsAdmin(async (token) => {
      const res = await this.adminFetch("policies/permissions-metadata", token);
      return res.json() as Promise<PermissionMetadataResponse>;
    });
  }

  async submitPassword(password: string): Promise<void> {
    if (!this.passwordRequest) return;
    this.passwordError = null;
    this.isWorking = true;

    try {
      this.storeAccessToken(await this.freshLogin(password));
      this.passwordRequest.resolve();
    } catch {
      this.forgetSession();
      this.passwordError = "Invalid admin password.";
      this.isWorking = false;
    }
  }

  cancelPasswordPrompt(): void {
    if (!this.passwordRequest) return;
    this.passwordRequest.reject(new PasswordPromptCancelled());
  }

  private async runAsAdmin<T>(
    action: (token: string) => Promise<T>,
  ): Promise<T> {
    this.isWorking = true;
    try {
      try {
        return await action(await this.ensureAccessToken());
      } catch (e) {
        if (!(e instanceof StaleTokenError)) throw e;
        // The cached token expired or lost its freshness; re-authenticate
        // behind the password prompt and replay the call once.
        this.forgetSession();
        return await action(await this.ensureAccessToken());
      }
    } finally {
      this.isWorking = false;
    }
  }

  private async ensureAccessToken(): Promise<string> {
    const stored = localStorage.getItem(accessTokenKey);
    if (stored) return stored;

    await this.waitForPassword();
    const token = localStorage.getItem(accessTokenKey);
    if (!token) throw new Error("Admin authentication failed");
    return token;
  }

  /**
   * Suspends until `submitPassword` resolves the deferred. While waiting,
   * `isWorking` flips back to `false` so the password dialog's Authenticate
   * button is interactive.
   */
  private async waitForPassword(): Promise<void> {
    this.isWorking = false;
    this.needsPassword = true;
    try {
      await new Promise<void>((resolve, reject) => {
        this.passwordRequest = { resolve, reject };
      });
    } finally {
      this.passwordRequest = null;
      this.needsPassword = false;
      this.passwordError = null;
      this.isWorking = true;
    }
  }

  /**
   * An admin call, outside ApiConnector so the logged-in user's session (and
   * its cookies) stay untouched. A 401 or 428 means the cached token is gone
   * or no longer fresh, which {@link runAsAdmin} recovers from; every other
   * failure becomes a RequestError, so it reads like any other API error.
   */
  private async adminFetch(
    path: string,
    token: string,
    init?: Omit<RequestInit, "headers" | "credentials">,
  ): Promise<Response> {
    const url = `${apiBase}/${path}`;
    const options: RequestInit = {
      ...init,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      credentials: "omit",
    };

    const res = await fetch(url, options);
    if (res.status === 401 || res.status === 428) throw new StaleTokenError();
    if (!res.ok) {
      const data: unknown = await res.json().catch(() => null);
      throw new RequestError(data, res, new Request(url, options));
    }
    return res;
  }

  /**
   * Re-authentication rather than a plain login: it yields a *fresh* access
   * token, which the policy update requires. The `admin` account is MFA-exempt,
   * so a challenge here means the deployment does not match this dev-tool.
   */
  private async freshLogin(password: string): Promise<string> {
    const res = await fetch(`${apiBase}/auth/fresh-login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        username: adminUsername,
        password,
      }),
      credentials: "omit",
    });
    if (!res.ok) throw new Error("Login failed");

    const data = (await res.json()) as PostApiV1AuthFreshLoginResponse;
    if (data.status !== "success") throw new Error("Login needs MFA");
    return data.access_token;
  }

  private forgetSession(): void {
    localStorage.removeItem(accessTokenKey);
  }

  private storeAccessToken(token: string): void {
    localStorage.setItem(accessTokenKey, token);
  }
}

class StaleTokenError extends Error {}
class PasswordPromptCancelled extends Error {}

const P = PermissionBit;

const ALL_PERMISSIONS = Object.values(PermissionBit).filter(
  (v): v is PermissionBit => typeof v === "number",
);

/**
 * Role → permission presets, mirroring `DEFAULT_ROLE_PERMISSIONS` in the backend.
 * Hand-maintained, so it can drift from the backend (see the in-sheet notice).
 *
 * @see {@link https://github.com/Seerlinq/db-api/blob/main/api/access_policy/permissions.py | db-api/api/access_policy/permissions.py}
 */
export const ROLE_PRESETS: Record<UserRole, PermissionBit[]> = {
  [UserRole["patient-ppg-app"]]: [P.VISIBILITY_SELF, P.GROUP_MEDICAL_ROUTERS],
  [UserRole["physician-ppg-app"]]: [
    P.VISIBILITY_MANAGED,
    P.GROUP_MEDICAL_ROUTERS,
  ],
  [UserRole["study-ppg-app"]]: [P.VISIBILITY_FULL, P.GROUP_MEDICAL_ROUTERS],
  [UserRole.patient]: [
    P.GROUP_MEDICAL_ROUTERS,
    P.UPDATE_OWNER,
    P.UPDATE_VISIBLE,
    P.READ,
    P.WRITE,
    P.VISIBILITY_SELF,
    P.PPG_META,
    P.ECG_META,
  ],
  [UserRole.physician]: [
    P.RUN_HEART_CORE_QUICKLOOK,
    P.PPG_RAW,
    P.ECG_RAW,
    P.GROUP_MEDICAL_ROUTERS,
    P.READ,
    P.WRITE,
    P.UPDATE_VISIBLE,
    P.DELETE_VISIBLE,
    P.VISIBILITY_MANAGED,
    P.VISIBILITY_PHYSICIAN,
  ],
  [UserRole["study-physician"]]: [
    P.RUN_HEART_CORE_QUICKLOOK,
    P.ACCESS_DRI,
    P.PPG_RAW,
    P.ECG_RAW,
    P.AFIB,
    P.GROUP_MONITORING_ROUTERS,
    P.GROUP_MEDICAL_ROUTERS,
    P.READ,
    P.WRITE,
    P.UPDATE_VISIBLE,
    P.DELETE_VISIBLE,
    P.VISIBILITY_MANAGED,
    P.VIEW_ANALYTICS,
  ],
  [UserRole["seerlinq-user"]]: [
    P.GROUP_MONITORING_ROUTERS,
    P.GROUP_MEDICAL_ROUTERS,
    P.READ,
    P.WRITE,
    P.UPDATE_VISIBLE,
    P.DELETE_VISIBLE,
    P.VISIBILITY_FULL,
    P.ACCESS_DRI,
    P.RUN_HEART_CORE_QUICKLOOK,
    P.ACCESS_LVFP,
    P.PPG_RAW,
    P.ECG_RAW,
    P.AFIB,
    P.VIEW_ANALYTICS,
    P.ACCESS_AI_CHAT,
  ],
  [UserRole["screening-user"]]: [
    P.GROUP_MEDICAL_ROUTERS,
    P.READ,
    P.PPG_META,
    P.VISIBILITY_PHYSICIAN,
    P.BYPASS_CONSENT,
    P.ALLOW_PAYMENT,
  ],
  // VISIBILITY_SELF is forbidden alongside VISIBILITY_FULL on the update path,
  // even though the backend grants it at user creation for admins.
  [UserRole.admin]: ALL_PERMISSIONS.filter((p) => p !== P.VISIBILITY_SELF),
};
